// Lane mapping — the canonical "which inbox tab does this chat file under?"
// module for the one-chat shell (Design Bible D1/D3/D4/D9/D20/D25/D29).
//
// A chat has exactly ONE origin (where it was born). The LANE (tab) it files
// under is VIEWER-RELATIVE: the same club-request thread reads "Club Req." for
// both sides, but an agent-request thread is the agent's "Agent Req." lane and
// the club's plain "DM" (the N2 case, D25). So lane = f(origin, perspective).
//
// This module derives origin from the data the backend already stamps on the
// Stream channel, with a robust fallback chain so alt/legacy channels still
// classify instead of vanishing. When be-core lands its canonical origin stamp
// (Bible V5), it is read FIRST (see ORIGIN_FIELD_CANDIDATES) — the heuristics
// below stay only as the safety net for older channels.

// Canonical thread ORIGINS. One per thread. Values are byte-identical to the
// backend enum stamped at channel.data._tp_custom_context.origin (Lead decree,
// coordinated with be-core V5) so BE and FE filter identically.
export const ORIGIN = {
  DM: "dm", // plain DM / identity-only, no transaction line
  LAST_MINUTE: "last_minute", // Last Minute listing reveal
  CLUB_REQUEST: "club_request", // club posted a request, agent responded
  AGENT_REQUEST: "agent_request", // agent posting / agent-tender board response
  SEARCH_REQUEST: "search_request", // club N3 search request (Trusted Agent)
  SCOUT_REQUEST: "scout_request", // "Contact recommender" scout recommendation
  STAFF_APPLICATION: "staff_application", // staff job-board application chat
  LAWYER_DIRECTORY: "lawyer_directory", // contact via the Lawyers directory
  PLAYER_CONTACT: "player_contact", // club/agent ↔ player contact
  ORGANIZER_CONTACT: "organizer_contact", // organiser contact
  FRIENDLY: "friendly", // friendlies contact (no dedicated tab → DM)
};

// Canonical LANE keys (inbox tabs). Byte-identical to the backend's lane_for
// output (be-core dashboard/chat_origins.py) so the `?tab=` notify-redirect
// deep-link contract matches: dm, last_minute, agent_requests, club_requests,
// scout_requests, trusted_agent.
export const LANE = {
  DM: "dm",
  LAST_MINUTE: "last_minute",
  AGENT_REQ: "agent_requests",
  CLUB_REQ: "club_requests",
  SCOUT_REQ: "scout_requests",
  TRUSTED_AGENT: "trusted_agent",
};

// The two inbox PERSPECTIVES D3/D4 define. Every platform role collapses to one
// of these for lane filing (or to DM-only, which never needs the resolver).
export const PERSPECTIVE = { AGENT: "agent", CLUB: "club" };

const ORIGIN_VALUES = new Set(Object.values(ORIGIN));

// Fold a raw string (canonical enum OR a "via …" label) to a canonical ORIGIN.
// Returns null when nothing recognizable is present.
const normalizeOrigin = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().replace(/^via\s+/, "").trim();
  if (!s) return null;
  // Bare canonical enum values pass straight through (the primary path).
  if (ORIGIN_VALUES.has(s)) return s;
  // Otherwise fold a human/legacy label. Order matters — most specific first.
  if (/scout|recommend/.test(s)) return ORIGIN.SCOUT_REQUEST;
  if (/search|trusted/.test(s)) return ORIGIN.SEARCH_REQUEST;
  if (/last[\s_-]?minute/.test(s)) return ORIGIN.LAST_MINUTE;
  if (/lawyer/.test(s)) return ORIGIN.LAWYER_DIRECTORY;
  if (/friendly|friendlies/.test(s)) return ORIGIN.FRIENDLY;
  if (/staff|application/.test(s)) return ORIGIN.STAFF_APPLICATION;
  // "agent request(s)" before the looser "agent" so it never falls through.
  if (/agent[\s_-]?request/.test(s)) return ORIGIN.AGENT_REQUEST;
  if (/club[\s_-]?request/.test(s)) return ORIGIN.CLUB_REQUEST;
  if (/direct|^dm$/.test(s)) return ORIGIN.DM;
  return null;
};

// deriveOrigin — best canonical origin for a channel from channel.data. Order:
//   1. the CANONICAL backend stamp channel.data._tp_custom_context.origin
//      (Lead decree — BE + FE read the same field, byte-identical).
//   2. legacy fallback: structural stamps unique to one creation path, then the
//      free-text request_origin_label / tp_context (alt channels predating V5).
//   3. default DM.
export const deriveOrigin = (channelData) => {
  const data = channelData || {};

  // 1) Canonical backend field (be-core V5).
  const canonical = normalizeOrigin(data._tp_custom_context?.origin);
  if (canonical) return canonical;

  // 2) Legacy fallback for alt channels with no canonical origin stamp.
  //    Scout / search ids are the most specific — check before the looser
  //    listing / tender_source signals so a scout-born LM chat still reads scout.
  if (data.scout_request_id != null || data.scout_recommendation_id != null) {
    return ORIGIN.SCOUT_REQUEST;
  }
  if (data.search_request_id != null || data.trusted_agent_request_id != null) {
    return ORIGIN.SEARCH_REQUEST;
  }
  if (data.lm_listing_id != null) return ORIGIN.LAST_MINUTE;
  // tender_source === "agent" is set ONLY by create_agent_response_channel — the
  // agent-tender board path (agent↔agent posting response).
  if (data.tender_source === "agent") return ORIGIN.AGENT_REQUEST;
  // A genuine club↔agent pitch (agent responding to a club tender) is a club
  // request. pitch identity + club_agent context, never the agent-tender case
  // (already caught above by tender_source).
  const hasPitchId =
    data.pitch_id != null ||
    data.pitch_details?.pitch_id != null ||
    data.pitch_details?.id != null;
  const cid = String(data.cid || data.id || "");
  const looksPitch = hasPitchId || /pitch-/.test(cid);
  if (looksPitch && data.tp_context === "club_agent") return ORIGIN.CLUB_REQUEST;

  // Free-text label (BE display hint) — parsed after structural stamps.
  const fromLabel = normalizeOrigin(data.request_origin_label);
  if (fromLabel) return fromLabel;

  // A club↔agent context with no other signal is still a club request thread
  // (some legacy pitch chats predate pitch_id); agent↔agent with no signal is a
  // colleague/direct DM.
  if (data.tp_context === "club_agent") return ORIGIN.CLUB_REQUEST;

  // 3) Default.
  return ORIGIN.DM;
};

// resolveLane — origin + viewer perspective → the tab the thread files under.
// The ONLY perspective-dependent origins are AGENT_REQUEST (agent → Agent Req.,
// club → DM = N2/D25) and SEARCH_REQUEST (agent → Agent Req., club → Trusted
// Agent). Everything else is perspective-agnostic.
export const resolveLane = (origin, perspective = PERSPECTIVE.AGENT) => {
  switch (origin) {
    case ORIGIN.LAST_MINUTE:
      return LANE.LAST_MINUTE;
    case ORIGIN.SCOUT_REQUEST:
      return LANE.SCOUT_REQ;
    case ORIGIN.CLUB_REQUEST:
      return LANE.CLUB_REQ;
    case ORIGIN.STAFF_APPLICATION:
      // Staff job-board application chats file under Club Req. (D20/D23 5h).
      return LANE.CLUB_REQ;
    case ORIGIN.AGENT_REQUEST:
      // D25: the club side of an agent request is an N2 chat → DM (with a "via
      // Agent Request" pill added by the preview); the agent side is Agent Req.
      return perspective === PERSPECTIVE.CLUB ? LANE.DM : LANE.AGENT_REQ;
    case ORIGIN.SEARCH_REQUEST:
      // Club owns the N3 search request → Trusted Agent; the responding agent
      // sees it in his Agent Req. lane (pill "via Search Request").
      return perspective === PERSPECTIVE.CLUB ? LANE.TRUSTED_AGENT : LANE.AGENT_REQ;
    case ORIGIN.DM:
    case ORIGIN.LAWYER_DIRECTORY:
    case ORIGIN.PLAYER_CONTACT:
    case ORIGIN.ORGANIZER_CONTACT:
    case ORIGIN.FRIENDLY:
      // DM-only origins (lawyer/player/organiser roles are DM-only; friendlies
      // have no dedicated tab → ride DM with a context pill).
      return LANE.DM;
    default:
      return LANE.DM;
  }
};

// classifyChannelLane — the one call the inbox uses: channel(.data) + viewer
// perspective → lane key. Fallback DM on any missing/garbage origin (Bible V5:
// "threads without origin fall back to DM").
export const classifyChannelLane = (channel, perspective = PERSPECTIVE.AGENT) => {
  const data = channel?.data || channel || {};
  const origin = deriveOrigin(data);
  return resolveLane(origin, perspective);
};

export default classifyChannelLane;
